import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEventId } from "../EventContext";
import { useAuth, Button, InputField } from "@vionixasia/shared-components";
import { useNotification } from "@vionixasia/shared-components/common/NotificationContext";
import { displayElapsed, durationToMs } from "../utils/finishTime";
import { collection, deleteDoc, doc, getDoc, getDocs, getFirestore, onSnapshot } from "firebase/firestore";

/**
 * Same wrapper AdminTiming uses, so labels here read like every other form in
 * livetiming rather than carrying their own one-off styling.
 */
const FormField = forwardRef(
  ({ className = "", wrapperClassName = "", ...props }, ref) => (
    <InputField
      ref={ref}
      labelColor="text-slate-700"
      className={className}
      wrapperClassName={wrapperClassName}
      {...props}
    />
  ),
);

/**
 * Time entry for buzzer-timed events — ninja/obstacle courses, speed climbing.
 *
 * Two athletes run at a time and the clock starts and stops on their own buzzer
 * press, so what the operator holds is an elapsed time, not a pair of clock
 * instants. The mat-timed input pages record a BIB against the moment the key
 * was pressed; that is the wrong shape here, and deriving a duration from an
 * invented start would only add a fake clock to a number already in hand.
 *
 * So this page does one thing: BIB plus duration in, stored verbatim. One time
 * per BIB — typing the same BIB again replaces it, which is what correcting a
 * mis-typed time looks like.
 */

const API = import.meta.env.VITE_API_BASE_URL;

/**
 * Digits typed into the time field → milliseconds.
 *
 * Digits fill from the LEFT against an hh:mm:ss template, so the first key an
 * operator presses is the hours digit: "1" is 10:00:00, "0123" is 01:23:00.
 * Untyped positions stay zero. That mirrors reading a race clock left to right
 * rather than a stopwatch counting up from hundredths.
 *
 * With `hundredths` the template is mm:ss.cc instead — a pool or sprint race
 * never reaches an hour, and its places are decided in the hundredths: "0123"
 * is 01:23.00, "012345" is 01:23.45.
 */
export const digitsToMs = (digits, hundredths = false) => {
  const clean = String(digits || "").replace(/\D/g, "").slice(0, 6);
  if (!clean) return null;
  const padded = clean.padEnd(6, "0");
  const a = Number(padded.slice(0, 2));
  const b = Number(padded.slice(2, 4));
  const c = Number(padded.slice(4, 6));
  if (hundredths) {
    if (b >= 60) return null;
    return (a * 60 + b) * 1000 + c * 10;
  }
  if (b >= 60 || c >= 60) return null;
  return (a * 3600 + b * 60 + c) * 1000;
};

/**
 * Digits → the text shown in the field, colons inserted as they are typed:
 * "" → "", "1" → "1", "12" → "12:", "1234" → "12:34:".
 * Separators appear only once the group before them is complete, so the caret
 * never sits behind punctuation the operator has to type around.
 */
export const formatDigits = (digits, hundredths = false) => {
  const clean = String(digits || "").replace(/\D/g, "").slice(0, 6);
  if (!clean) return "";
  const groups = [clean.slice(0, 2), clean.slice(2, 4), clean.slice(4, 6)].filter(Boolean);
  // mm:ss.cc puts a point, not a colon, before the last pair.
  const seps = hundredths ? [":", "."] : [":", ":"];
  const joined = groups.reduce((out, group, i) => (i === 0 ? group : `${out}${seps[i - 1]}${group}`), "");
  // Trailing separator once a pair is complete and there is room for more, so
  // the field reads 12: rather than 12 while the next pair is being entered.
  return clean.length < 6 && clean.length % 2 === 0 ? `${joined}${seps[clean.length / 2 - 1]}` : joined;
};

/** Milliseconds → the hh:mm:ss.SSS the API stores. */
export const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const total = Math.round(ms);
  const s = Math.floor(total / 1000);
  const pad = (n, w = 2) => String(n).padStart(w, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}.${pad(total % 1000, 3)}`;
};


export const InputNinja = () => {
  const { realEventId: eventId } = useEventId();
  const { getIdToken } = useAuth();

  const [bib, setBib] = useState("");
  const [duration, setDuration] = useState("");
  const [participants, setParticipants] = useState([]);
  const [recorded, setRecorded] = useState([]);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const { showConfirm } = useNotification();
  const bibRef = useRef(null);
  const timeRef = useRef(null);

  const authenticatedFetch = useCallback(
    async (url, options = {}) => {
      const token = await getIdToken();
      return fetch(url, {
        ...options,
        headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
      });
    },
    [getIdToken],
  );

  // Start list, categories and recorded times are read from Firestore, not the
  // API: the backend may be asleep in standby, and the recorded list has to
  // reflect a delete at once (the API caches results for a few seconds).
  useEffect(() => {
    if (!eventId) return;
    getDocs(collection(getFirestore(), "event_timing", eventId, "participants"))
      .then((snapshot) => setParticipants(snapshot.docs.map((d) => d.data())))
      .catch(() => setParticipants([]));
  }, [eventId]);

  // Categories are loaded for display (labels, precision of shown times).
  useEffect(() => {
    if (!eventId) return;
    getDoc(doc(getFirestore(), "event_timing", eventId))
      .then((snapshot) => setCategories(snapshot.data()?.categories || []))
      .catch(() => setCategories([]));
  }, [eventId]);

  const categoryById = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );
  // Time is always keyed as hh:mm:ss, six digits, hours first — how the
  // operators read the clock. A brief mm:ss.cc template for hundredths
  // categories had them file 7:46 as 7.46 seconds mid-race at AquaFitX.
  const hundredths = false;
  // The category a row belongs to, short form: "30-39", "Double Mixed". For
  // the individual categories this is the age group the athlete is ranked in.
  const categoryLabel = (categoryId, ageGroup) =>
    ageGroup || (categoryById.get(categoryId)?.name || categoryId || "-").replace(/^INDIVIDUAL |^TEAM /, "");

  const shownTime = (value, categoryId) => {
    const category = categoryById.get(categoryId) || null;
    // hh:mm:ss, the format the time is keyed in — "06:14" read as six hours.
    return displayElapsed(value, category);
  };

  // Live: a save, an edit or a delete from any operator's screen shows here
  // without a reload.
  useEffect(() => {
    if (!eventId) return undefined;
    return onSnapshot(collection(getFirestore(), "event_timing", eventId, "results"), (snapshot) =>
      setRecorded(snapshot.docs.map((d) => ({ bib: d.id, ...d.data() }))),
    );
  }, [eventId]);

  const participantByBib = useMemo(() => {
    const map = new Map();
    participants.forEach((p) => map.set(String(p.bib), p));
    return map;
  }, [participants]);

  const matched = bib ? participantByBib.get(String(bib).trim()) : null;
  const parsedMs = digitsToMs(duration, hundredths);
  // An existing time is worth surfacing before the operator commits: saving is
  // a replace, and it should never be a surprise.
  const existing = recorded.find((r) => String(r.bib) === String(bib).trim());

  // Newest entry first: what the operator just saved is what they check, and
  // sorted by time it landed somewhere in the middle of the list.
  const leaderboard = useMemo(
    () =>
      recorded
        .filter((r) => r.finishDuration)
        .slice()
        .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""))),
    [recorded],
  );

  /** A stored duration → the digits the time field holds, in its template. */
  const durationToDigits = (value) => {
    const ms = durationToMs(String(value || ""));
    if (!Number.isFinite(ms)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const s = Math.floor(ms / 1000);
    return hundredths
      ? `${pad(Math.floor(s / 60))}${pad(s % 60)}${pad(Math.floor((ms % 1000) / 10))}`
      : `${pad(Math.floor(s / 3600))}${pad(Math.floor((s % 3600) / 60))}${pad(s % 60)}`;
  };

  // Edit = load the row into the form; saving it replaces the time.
  const editRow = (row) => {
    setBib(String(row.bib));
    setDuration(durationToDigits(row.recordedDuration || row.finishDuration));
    setStatus(null);
    timeRef.current?.focus();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Delete is for a time filed under the wrong BIB: that runner goes back to
  // having no time. Confirmed first through the app's global confirm modal,
  // because it cannot be undone from here.
  const rowLabel = (row) =>
    `BIB ${row.bib} · ${row.name || ""} · ${shownTime(row.finishDuration, row.categoryId)}`;
  const deleteRow = async (row) => {
    const label = rowLabel(row);
    setDeleting(String(row.bib));
    try {
      await deleteDoc(doc(getFirestore(), "event_timing", eventId, "results", String(row.bib)));
      setStatus({ tone: "success", message: `Deleted: ${label}` });
    } catch (error) {
      setStatus({ tone: "error", message: `Delete failed: ${error.message}` });
    } finally {
      setDeleting(null);
    }
  };

  const submit = async (event) => {
    event?.preventDefault();
    const bibKey = String(bib).trim();
    if (!bibKey) {
      setStatus({ tone: "error", message: "BIB is required" });
      return;
    }
    if (parsedMs === null) {
      setStatus({
        tone: "error",
        message: hundredths ? "Enter a time — seconds must be under 60" : "Enter a time — minutes and seconds must be under 60",
      });
      return;
    }

    setSaving(true);
    setStatus(null);
    try {
      const response = await authenticatedFetch(`${API}/api/timing/record-duration`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventId, bib: bibKey, duration: formatDuration(parsedMs) }),
      });
      const data = await response.json();
      if (!response.ok) {
        setStatus({ tone: "error", message: data?.message || "Failed to record time" });
      } else {
        setStatus({
          tone: "success",
          message: `BIB ${bibKey} · ${shownTime(data.duration, matched?.categoryId)}${existing ? " (replaced)" : ""}`,
        });
        setBib("");
        setDuration("");
        bibRef.current?.focus();
      }
    } catch (error) {
      setStatus({ tone: "error", message: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    // Same shell as the results page, so this page sits inside the app Layout
    // with the shared navbar and container width rather than its own.
    <section className="min-h-screen mt-10 bg-white mb-10">
      <div className="container mx-auto px-4 space-y-4">
        <header>
          <h1 className="text-2xl font-bold text-slate-900 font-oxanium">Ninja time entry</h1>
          <p className="text-sm text-slate-500">
            Type the BIB, then key the time as digits in {hundredths ? "mm:ss.cc" : "hh:mm:ss"} order —
            separators are added for you. One time per BIB; entering a BIB again replaces its time.
          </p>
        </header>

        <form
          onSubmit={submit}
          className="bg-white rounded-card border border-slate-200 p-4"
        >
          {/* BIB, time and Save on one row: the operator reads a number off the
              clock and commits it in a single left-to-right pass. The button is
              a fixed-width column so the two fields share the rest. */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 md:items-end">
            <FormField
              ref={bibRef}
              label="BIB"
              name="ninja-bib"
              value={bib}
              onChange={(e) => setBib(e.target.value.replace(/\s/g, ""))}
              inputMode="numeric"
              autoFocus
              placeholder="101"
              className="text-lg font-bold tabular-nums"
            />

            {/* The field shows colons but stores only digits, filling the
                hh:mm:ss template from the left — the first key is the hours
                digit. Backspace removes a digit, not a separator, so the
                operator never has to type around punctuation. */}
            <FormField
              ref={timeRef}
              label="Time"
              name="ninja-time"
              value={formatDigits(duration, hundredths)}
              onChange={(e) => setDuration(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              placeholder={hundredths ? "00:00.00" : "00:00:00"}
              error={duration && parsedMs === null ? true : undefined}
              className="text-lg font-bold tabular-nums"
            />

            <Button type="submit" color="cyan" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>

          {/* Echo the interpretation back before saving: "1:23.4" meaning
              83.400s rather than 1m23.4s is exactly the kind of mistake that is
              invisible until the results are published. */}
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            {bib && (
              <span className={matched ? "text-slate-600" : "text-amber-600"}>
                {matched ? matched.name || `BIB ${bib}` : `BIB ${bib} not in start list`}
              </span>
            )}
            {existing && (
              <span className="text-amber-600">
                already has {shownTime(existing.finishDuration, existing.categoryId)} — saving replaces it
              </span>
            )}
          </div>

          {status && (
            <p
              className={`mt-4 rounded-xl px-3 py-2 text-sm font-medium ${
                status.tone === "error"
                  ? "bg-red-50 text-red-700"
                  : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {status.message}
            </p>
          )}
        </form>

        {/* Standings, laid out like the venue board: a labelled header row, then
            rows separated by a divider. BIB gets its own column rather than
            being stacked under the name — the operator scans BIBs to check what
            has already been entered, and a subtitle is the wrong place for that. */}
        <section className="bg-white rounded-card border border-slate-200 overflow-hidden">
          <div className="flex items-baseline justify-between px-4 pt-4 pb-3">
            <h2 className="text-lg font-bold text-slate-900 font-oxanium">Recorded</h2>
            <span className="text-sm text-slate-500">{leaderboard.length} times</span>
          </div>

          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th className="border-y border-slate-200 px-4 py-2 text-left w-14">#</th>
                <th className="border-y border-slate-200 px-4 py-2 text-left">Athlete</th>
                <th className="border-y border-slate-200 px-4 py-2 text-center">Bib</th>
                <th className="border-y border-slate-200 px-4 py-2 text-center">Age Group</th>
                <th className="border-y border-slate-200 px-4 py-2 text-right">Time</th>
                <th className="border-y border-slate-200 px-4 py-2 w-40" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leaderboard.map((row, index) => (
                <tr key={row.bib}>
                  <td className="px-4 py-2.5 text-sm font-bold text-slate-400">{index + 1}</td>
                  <td className="px-4 py-2.5 font-semibold text-slate-800 max-w-0 truncate">
                    {row.name || "-"}
                  </td>
                  <td className="px-4 py-2.5 text-center text-sm text-slate-500 tabular-nums">
                    {row.bib}
                  </td>
                  <td className="px-4 py-2.5 text-center text-sm text-slate-500 uppercase whitespace-nowrap">
                    {categoryLabel(row.categoryId, row.ageGroup || participantByBib.get(String(row.bib))?.ageGroup)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-lg font-bold tabular-nums text-slate-900">
                    {shownTime(row.finishDuration, row.categoryId)}
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => editRow(row)}
                      className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        showConfirm(rowLabel(row), () => deleteRow(row), "Delete", "Cancel", "Delete this time?")
                      }
                      disabled={deleting === String(row.bib)}
                      className="ml-2 rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      {deleting === String(row.bib) ? "Deleting…" : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
              {!leaderboard.length && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-sm text-slate-400">
                    No times recorded yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </section>
  );
};

export default InputNinja;
